import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import DatePicker from 'react-datepicker';
import { fi } from 'date-fns/locale';
import {
  Accordion,
  Button,
  Card,
  Container,
  Form,
  Table,
} from 'react-bootstrap';
import * as signalR from '@microsoft/signalr';
import { format } from 'date-fns';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { InputCheck, SectionHeader } from '../../common/components';
import { TagSelector } from '../../common/components/TagSelector';
import { AddonSelector } from '../../common/components/AddonSelector';
import {
  clearSeasonRateBatchUpdateDetails,
  fetchLastTagOperation,
  fetchSeasonRateBatchUpdateDetails,
  type LastTagOperationDto,
  modifyAdditionalServiceRulesForSeasonRates,
} from './rateToolsSlice';
import LastTagOperationInfo from './LastTagOperationInfo';
import { formatDateToFinnish } from '../../common/utils/dateUtils';
import signalRPricingService from '../../common/hubs/signalRPricingService';
import {
  SEASON_RATE_BATCH_OPERATION_TYPE,
  SEASON_RATE_BATCH_OPERATION_TYPE_DESCRIPTIONS,
} from '../../common/constants';

interface CopyMessage {
  accommodationNumber?: string;
  accommodationName?: string;
  seasonRatesChangedCount?: string;
}

const ModifyAddonRulesTool: React.FC = () => {
  const dispatch = useAppDispatch();
  const [selectedTagId, setSelectedTagId] = useState<string | null>(null);
  const [lastTagOperation, setLastTagOperation] =
    useState<LastTagOperationDto | null>(null);
  const [selectedAddonId, setSelectedAddonId] = useState<string | null>(null);
  const [selectedStartDateForTag, setSelectedStartDateForTag] =
    useState<Date | null>(null);
  const [selectedEndDateForTag, setSelectedEndDateForTag] =
    useState<Date | null>(null);
  const [isMandatory, setIsMandatory] = useState<boolean | null>(true);
  const [priceChangePercentValue, setPriceChangePercentValue] = useState<
    number | null
  >(null);
  const [priceChangeAbsoluteValue, setPriceChangeAbsoluteValue] = useState<
    number | null
  >(null);

  const [copyMessages, setCopyMessages] = useState<CopyMessage[]>([]);
  const [isCopying, setIsCopying] = useState<boolean>(false);
  const seasonRateBatchUpdateDetails = useAppSelector(
    state => state.rateTools.seasonRateBatchUpdateDetails,
  );

  useEffect(() => {
    dispatch(clearSeasonRateBatchUpdateDetails());
    dispatch(
      fetchSeasonRateBatchUpdateDetails(
        SEASON_RATE_BATCH_OPERATION_TYPE.AdditionalServiceRules,
      ),
    );
  }, [dispatch]);

  useEffect(() => {
    if (
      signalRPricingService.connection.state ===
      signalR.HubConnectionState.Disconnected
    ) {
      console.log('start connection');
      signalRPricingService.startConnection();
    }

    signalRPricingService.onReceiveMessage = (
      accommodationNumber: string,
      accommodationName: string,
      seasonRatesChangedCount: string,
    ) => {
      setCopyMessages(prevCopyMessages => [
        ...prevCopyMessages,
        { accommodationNumber, accommodationName, seasonRatesChangedCount },
      ]);
    };
  }, []);

  const handleTagSelected = async (tagId: string) => {
    setSelectedTagId(tagId);

    if (tagId === null) {
      setLastTagOperation(null);
    } else {
      const result = await dispatch(
        fetchLastTagOperation({
          tagId,
          operationType:
            SEASON_RATE_BATCH_OPERATION_TYPE.AdditionalServiceRules,
        }),
      );
      setLastTagOperation(
        (result.payload as LastTagOperationDto | null) ?? null,
      );
    }
  };

  const handleAddonSelected = (addonId: string) => {
    setSelectedAddonId(addonId);
  };

  const handleAddonServiceAddingForTag = async () => {
    if (
      selectedTagId &&
      selectedAddonId &&
      selectedStartDateForTag &&
      selectedEndDateForTag
    ) {
      setIsCopying(true);
      await dispatch(
        modifyAdditionalServiceRulesForSeasonRates({
          tagId: selectedTagId,
          addonServiceId: selectedAddonId,
          isDelete: false,
          modifyRulesFrom: formatDateToFinnish(selectedStartDateForTag),
          modifyRulesTo: formatDateToFinnish(selectedEndDateForTag),
          priceChangePercentValue,
          priceChangeAbsoluteValue,
          isMandatory,
        }),
      );
      setIsCopying(false);
    }
  };

  const handleAddonServiceRemovalForTag = async () => {
    if (
      selectedTagId &&
      selectedAddonId &&
      selectedStartDateForTag &&
      selectedEndDateForTag
    ) {
      setIsCopying(true);
      await dispatch(
        modifyAdditionalServiceRulesForSeasonRates({
          tagId: selectedTagId,
          addonServiceId: selectedAddonId,
          isDelete: true,
          modifyRulesFrom: formatDateToFinnish(selectedStartDateForTag),
          modifyRulesTo: formatDateToFinnish(selectedEndDateForTag),
          priceChangePercentValue,
          priceChangeAbsoluteValue,
          isMandatory,
        }),
      );
      setIsCopying(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Hinnoittelu - Lisäpalvelusääntöjen lisäys / poisto</title>
      </Helmet>

      <Container className="container-content">
        <SectionHeader
          Breadcrumbs={['Hinnoittelu']}
          Title="Lisäpalvelusääntöjen lisäys / poisto"
        />
        <div className="my-48">
          <div
            className="border bg-white rounded shadow-lg mx-auto p-24 p-xl-32"
            style={{ maxWidth: 640 }}
          >
            <TagSelector
              filterActiveAccommodations
              Subtitle="Lisäpalvelusääntöjen muutos tagin kohteille"
              Title="Valitse tagi"
              onTagSelected={handleTagSelected}
            />
            <LastTagOperationInfo lastOperation={lastTagOperation} />
            {selectedTagId && (
              <div className="mt-16">
                <AddonSelector
                  Subtitle="Lisäpalvelun valinta"
                  Title="Valitse lisäpalvelu"
                  onAddonSelected={handleAddonSelected}
                />
              </div>
            )}
            {selectedTagId && selectedAddonId && (
              <>
                <p className="text-gray-600 mt-16 mb-16">
                  Lisäpalvelusääntöjen alkupäivämäärä
                </p>
                <DatePicker
                  locale={fi}
                  dateFormat="dd.MM.yyyy"
                  onChange={date => setSelectedStartDateForTag(date)}
                  className="form-control"
                  selected={selectedStartDateForTag}
                  showWeekNumbers
                  showYearDropdown
                  scrollableYearDropdown
                />
                <p className="text-gray-600 mt-16 mb-16">
                  Lisäpalvelusääntöjen päättymispäivämäärä
                </p>
                <DatePicker
                  locale={fi}
                  dateFormat="dd.MM.yyyy"
                  onChange={date => setSelectedEndDateForTag(date)}
                  className="form-control"
                  selected={selectedEndDateForTag}
                  minDate={selectedStartDateForTag ?? null}
                  disabled={!selectedStartDateForTag}
                  showYearDropdown
                  showWeekNumbers
                  scrollableYearDropdown
                />
                <p className="text-gray-600 mt-16 mb-16">
                  Hinnan muutosprosentti
                </p>
                <Form.Control
                  type="number"
                  id="priceChangePercentValue"
                  name="priceChangePercentValue"
                  className="mb-16"
                  onChange={e => {
                    setPriceChangePercentValue(
                      e.target.value ? parseFloat(e.target.value) : null,
                    );
                    setPriceChangeAbsoluteValue(null);
                  }}
                  value={priceChangePercentValue || ''}
                />
                <p className="text-gray-600 mt-16 mb-16">
                  Hinnan kiinteä muutos
                </p>
                <Form.Control
                  type="number"
                  id="priceChangeAbsoluteValue"
                  name="priceChangeAbsoluteValue"
                  className="mb-16"
                  onChange={e => {
                    setPriceChangeAbsoluteValue(
                      e.target.value ? parseFloat(e.target.value) : null,
                    );
                    setPriceChangePercentValue(null);
                  }}
                  value={priceChangeAbsoluteValue || ''}
                />
                <InputCheck
                  Id="isMandatory"
                  Name="isMandatory"
                  Label="Pakollinen lisäpalvelu"
                  HandleChange={e => setIsMandatory(e.target.checked)}
                  Checked={isMandatory}
                  IsRequired={false}
                />
                <Button
                  variant="primary"
                  className="mt-16 mb-16 w-100"
                  onClick={handleAddonServiceAddingForTag}
                  disabled={isCopying}
                >
                  {isCopying
                    ? 'Lisätään sääntöjä...'
                    : 'Lisää säännöt tagin kohteille'}
                </Button>
                <Button
                  variant="danger"
                  className="mt-16 mb-16 w-100"
                  onClick={handleAddonServiceRemovalForTag}
                  disabled={isCopying}
                >
                  {isCopying
                    ? 'Poistetaan sääntöjä...'
                    : 'Poista säännöt tagin kohteilta'}
                </Button>
              </>
            )}
          </div>
        </div>

        {copyMessages.length > 0 && (
          <Card className="bg-white border shadow-none">
            <h6 className="text-uppercase text-muted m-2">
              Lisäpalvelusääntöjen muutokset
            </h6>
            <Table striped bordered hover>
              <thead>
                <tr>
                  <th>Kohteen numero</th>
                  <th>Kohteen nimi</th>
                  <th>Kausia päivitetty</th>
                </tr>
              </thead>
              <tbody>
                {copyMessages.map(copyMessage => (
                  <tr key={`${copyMessage.accommodationNumber}`}>
                    <td>{copyMessage.accommodationNumber}</td>
                    <td>{copyMessage.accommodationName}</td>
                    <td>{copyMessage.seasonRatesChangedCount}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card>
        )}

        {seasonRateBatchUpdateDetails && (
          <Accordion>
            {seasonRateBatchUpdateDetails.map((detail, index) => (
              <Accordion.Item
                eventKey={index.toString()}
                key={detail.operationType}
              >
                <Accordion.Header>
                  <div>
                    <h6 className="text-uppercase text-muted m-2">
                      {
                        SEASON_RATE_BATCH_OPERATION_TYPE_DESCRIPTIONS[
                          detail.operationType
                        ]
                      }{' '}
                      -{' '}
                      {format(
                        new Date(detail.operationDate),
                        'dd.MM.yyyy HH:mm:ss',
                      )}
                    </h6>
                    {detail.requestParametersDescription && (
                      <div className="text-muted small ms-2 mb-1">
                        {detail.requestParametersDescription}
                      </div>
                    )}
                  </div>
                </Accordion.Header>
                <Accordion.Body>
                  <Card className="bg-white border shadow-none p-24 p-xl-32">
                    <strong>Valitut parametrit:</strong>{' '}
                    {detail.requestParametersDescription}
                    {detail.updateDetails &&
                      detail.updateDetails.length > 0 && (
                        <Table striped bordered hover>
                          <thead>
                            <tr>
                              <th>Kohteen numero</th>
                              <th>Kohteen nimi</th>
                              <th>Kausia muutettu</th>
                            </tr>
                          </thead>
                          <tbody>
                            {detail.updateDetails.map(updateDetail => (
                              <tr key={updateDetail.accommodationNumber}>
                                <td>{updateDetail.accommodationNumber}</td>
                                <td>{updateDetail.accommodationName}</td>
                                <td>{updateDetail.seasonRatesChangedCount}</td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      )}
                  </Card>
                </Accordion.Body>
              </Accordion.Item>
            ))}
          </Accordion>
        )}
      </Container>
    </>
  );
};

export default ModifyAddonRulesTool;
